import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/stores/authStore";
import { userService } from "@/services/userService";
import { getStatusMessage } from "@/utils/messageHelper";
import { authService } from "@/services/authService";
import { Permission } from "@/utils/permissions";
import { showToast } from "@/lib/toast";

export default function ProfilePage() {
  const { user } = useAuthStore();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [roleName, setRoleName] = useState("");
  const [createdAt, setCreatedAt] = useState<string | undefined>(undefined);
  const [updatedAt, setUpdatedAt] = useState<string | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [permissions, setPermissions] = useState<Permission[]>([]);

  useEffect(() => {
    // ưu tiên data từ API /auth/me để luôn mới nhất
    const load = async () => {
      const me = await authService.getUserInfo("");
      if (me) {
        setName(me?.name || "");
        setEmail(me?.email || "");
        setUsername(me?.username || "");
        setRoleName(me?.role?.name || "");
        setCreatedAt(me?.createdAt);
        setUpdatedAt(me?.updatedAt);
        if (Array.isArray(me?.permission))
          setPermissions(me.permission as Permission[]);
        return;
      }
      if (user) {
        setName(user.name || "");
        setEmail(user.email || "");
        setUsername(user.username || "");
        setRoleName(user.role?.name || "");
      }
    };
    load();
  }, [user]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const res: any = await userService.updateMe({ name, email });
      if ((res as any)?.ok) {
        showToast.success("Cập nhật hồ sơ thành công");
      } else if ((res as any)?.status || (res as any)?.data?.status) {
        const status = (res as any)?.status || (res as any)?.data?.status;
        const rawMessage =
          (res as any)?.message ||
          (res as any)?.data?.message ||
          "Cập nhật hồ sơ thất bại";
        const viMessage = getStatusMessage(status as any, rawMessage);
        showToast.error(`${viMessage} (${status})`);
      } else {
        const statusCode = (res as any)?.data?.statusCode;
        if (statusCode && statusCode !== 200) {
          const message =
            (res as any)?.data?.message || "Cập nhật hồ sơ thất bại";
          showToast.error(`${message} (${statusCode})`);
        } else {
          showToast.success("Cập nhật hồ sơ thành công");
        }
      }
    } catch (e: any) {
      const apiMsg = e?.response?.data?.message;
      const apiStatus =
        e?.response?.data?.status || e?.response?.data?.statusCode;
      const viMessage = apiStatus
        ? getStatusMessage(apiStatus as any, apiMsg)
        : apiMsg || "Có lỗi xảy ra";
      showToast.error(`${viMessage}${apiStatus ? ` (${apiStatus})` : ""}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Hồ sơ cá nhân</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Tên đăng nhập
                </label>
                <Input value={username} disabled readOnly />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Vai trò
                </label>
                <Input value={roleName} disabled readOnly />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Họ và tên
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nhập họ tên"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
            {(createdAt || updatedAt) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-500">
                {createdAt && (
                  <div>
                    <span className="font-medium">Tạo lúc: </span>
                    {new Date(createdAt).toLocaleString("vi-VN")}
                  </div>
                )}
                {updatedAt && (
                  <div>
                    <span className="font-medium">Cập nhật lúc: </span>
                    {new Date(updatedAt).toLocaleString("vi-VN")}
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end">
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="cursor-pointer"
              >
                {isSaving ? "Đang lưu..." : "Lưu thay đổi"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quyền hạn</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-500 mb-3">
              Danh sách quyền hạn của bạn trong hệ thống
            </p>
            <div className="space-y-2">
              {permissions.length === 0 ? (
                <div className="text-sm text-gray-500">Không có quyền nào</div>
              ) : (
                permissions.map((p) => {
                  const subjectMap: Record<string, string> = {
                    all: "Tất cả",
                    log: "Nhật ký",
                    user: "Người dùng",
                    role: "Vai trò",
                    information: "Thông tin",
                    category: "Danh mục",
                    article: "Bài viết",
                    image: "Hình ảnh",
                    page: "Trang",
                    blog_post: "Blog Post",
                    menu: "Menu",
                    film_list: "Danh sách phim",
                    author: "Tác giả",
                    robot_txt: "Robot.txt",
                    widget: "Widget",
                    comment: "Bình luận",
                  };
                  const actionMap: Record<string, string> = {
                    create: "Tạo",
                    read: "Xem",
                    update: "Sửa",
                    delete: "Xóa",
                    manage: "Toàn quyền",
                  };
                  const subjectVi = subjectMap[p.subject] || p.subject;
                  const actionsVi = (p.action || [])
                    .map((a) => actionMap[a] || a)
                    .join(", ");
                  const isFullAccess =
                    p.subject === "all" && (p.action || []).includes("manage");
                  return (
                    <div
                      key={p._id}
                      className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2"
                    >
                      <div className="text-sm">
                        <span className="font-medium">{subjectVi}</span>{" "}
                        <span className="text-gray-500">({actionsVi})</span>
                      </div>
                      {isFullAccess && (
                        <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-600">
                          Toàn quyền
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
